import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton,
  CircularProgress, Chip, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Dialog, DialogTitle,
  DialogContent, TextField, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Map as MapIcon, Refresh, LocationOn, Battery3Bar,
  SignalCellularAlt, Speed, DirectionsCar, Close,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

/**
 * Vehicle Locations Dashboard
 * Real-time tracking of 1000+ devices on map
 * 
 * Features:
 * - Live GPS locations for all vehicles
 * - Battery and signal strength monitoring
 * - Speed and heading display
 * - Location history tracking
 * - Map integration (Leaflet/Google Maps)
 */

const VehicleLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds

  // ── Fetch all active vehicle locations ──────────────────────────────────────
  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await InvokeApi(Config.base_url + '/device/locations/active', {});
      if (res?.code === '200') {
        setLocations(res.locations || []);
      }
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Auto-refresh locations ─────────────────────────────────────────────────
  useEffect(() => {
    fetchLocations();
    if (!autoRefresh) return;

    const interval = setInterval(fetchLocations, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchLocations]);

  // ── Fetch location history for a vehicle ───────────────────────────────────
  const handleViewHistory = async (vehicleId) => {
    setSelectedVehicle(vehicleId);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await InvokeApi(Config.base_url + '/device/location/history', {
        vehicle_id: vehicleId,
        limit: 100,
        hours: 24,
      });
      if (res?.code === '200') {
        setHistory(res.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ── Format time ────────────────────────────────────────────────────────────
  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleString();
  };

  // ── Get battery status color ────────────────────────────────────────────────
  const getBatteryColor = (battery) => {
    if (battery >= 80) return 'success';
    if (battery >= 50) return 'warning';
    return 'error';
  };

  // ── Get status color ────────────────────────────────────────────────────────
  const getStatusColor = (speed) => {
    if (speed > 20) return '#ff6b35'; // orange - moving
    if (speed > 0) return '#ffd700'; // yellow - slow
    return '#4CAF50'; // green - stationary
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">
            <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
            Vehicle Locations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time tracking for {locations.length} active vehicles
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh Now">
            <IconButton onClick={fetchLocations} disabled={loading} sx={{ color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant={autoRefresh ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
            sx={{ color: autoRefresh ? '#fff' : '#1a237e', backgroundColor: autoRefresh ? '#1a237e' : 'transparent' }}
          >
            {autoRefresh ? '⏱️ Auto ON' : 'Auto OFF'}
          </Button>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Refresh</InputLabel>
            <Select value={refreshInterval} label="Refresh" onChange={(e) => setRefreshInterval(e.target.value)}>
              <MenuItem value={10}>10s</MenuItem>
              <MenuItem value={30}>30s</MenuItem>
              <MenuItem value={60}>1m</MenuItem>
              <MenuItem value={300}>5m</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Map View Card */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, bgcolor: '#f5f6fa', borderRadius: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <MapIcon sx={{ fontSize: 48, color: '#1a237e', mb: 1 }} />
              <Typography variant="subtitle1" color="text.secondary">
                Map View (Coming Soon)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Real-time map with vehicle markers, routes, and geofencing
              </Typography>
              <Button variant="contained" size="small" sx={{ mt: 2, backgroundColor: '#1a237e' }} onClick={() => setMapOpen(true)}>
                Open Map
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Vehicles', value: locations.length, icon: '🚗', color: '#1a237e' },
          {
            label: 'Moving',
            value: locations.filter((l) => (l.speed || 0) > 20).length,
            icon: '🏃',
            color: '#ff6b35',
          },
          {
            label: 'Idle',
            value: locations.filter((l) => (l.speed || 0) === 0).length,
            icon: '⏸️',
            color: '#4CAF50',
          },
          {
            label: 'Low Battery',
            value: locations.filter((l) => (l.battery || 0) < 50).length,
            icon: '🔋',
            color: '#f44336',
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ borderRadius: 2, background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`, border: `2px solid ${stat.color}` }}>
              <CardContent sx={{ py: 1.5, px: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                  {stat.icon} {stat.label}
                </Typography>
                <Typography variant="h6" fontWeight={700} color={stat.color}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Locations Table */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {[
                    'Vehicle',
                    'Model',
                    'GPS Location',
                    'Speed',
                    'Heading',
                    'Battery',
                    'Signal',
                    'Last Update',
                    'Actions',
                  ].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      No vehicles with active GPS data
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map((loc, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DirectionsCar sx={{ color: '#1a237e', fontSize: 20 }} />
                          <Typography fontWeight={600} fontSize={13}>
                            {loc.vehicle_number}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell fontSize={12}>{loc.model || '—'}</TableCell>
                      <TableCell fontSize={12} sx={{ fontFamily: 'monospace' }}>
                        <Tooltip title={`${parseFloat(loc.latitude).toFixed(6)}, ${parseFloat(loc.longitude).toFixed(6)}`}>
                          <span>
                            {parseFloat(loc.latitude)?.toFixed(4)}, {parseFloat(loc.longitude)?.toFixed(4)}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<Speed sx={{ fontSize: 14 }} />}
                          label={`${loc.speed || 0} km/h`}
                          size="small"
                          sx={{
                            background: getStatusColor(loc.speed),
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell fontSize={12}>{loc.heading || 0}°</TableCell>
                      <TableCell>
                        <Chip
                          icon={<Battery3Bar sx={{ fontSize: 14 }} />}
                          label={`${loc.battery || 0}%`}
                          size="small"
                          color={getBatteryColor(loc.battery)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<SignalCellularAlt sx={{ fontSize: 14 }} />}
                          label={`${loc.signal_strength || 0}%`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell fontSize={11}>{formatTime(loc.received_at)}</TableCell>
                      <TableCell>
                        <Tooltip title="View History">
                          <IconButton size="small" onClick={() => handleViewHistory(loc.vehicle_id)} sx={{ color: '#1a237e' }}>
                            <MapIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>
          Location History (24 hours)
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['Timestamp', 'Location', 'Speed', 'Heading', 'Accuracy'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No history data
                      </TableCell>
                    </TableRow>
                  ) : (
                    history.map((h, i) => (
                      <TableRow key={i}>
                        <TableCell fontSize={11}>{formatTime(h.received_at)}</TableCell>
                        <TableCell fontSize={11} sx={{ fontFamily: 'monospace' }}>
                          {parseFloat(h.latitude)?.toFixed(6)}, {parseFloat(h.longitude)?.toFixed(6)}
                        </TableCell>
                        <TableCell fontSize={11}>{h.speed} km/h</TableCell>
                        <TableCell fontSize={11}>{h.heading}°</TableCell>
                        <TableCell fontSize={11}>±{h.accuracy}m</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default VehicleLocations;
